import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  OnDestroy,
  OnInit,
  OnChanges,
} from '@angular/core';
import { UploadService } from '@app/scaffold/services/upload.service';
import { environment } from '@env/environment';
import { HttpUtilService } from '@services/http-util.service';
import { NotificationsService } from '@services/notifications.service';
import {
  catchError,
  exhaustMap,
  map,
  Observable,
  of,
  Subject,
  Subscription,
} from 'rxjs';

export interface UploadFile extends File {
  progress: number;
  error?: any;
  isUploading: boolean;
  key?: string;
  databaseEntryAttempt?: boolean;
}

@Component({
  selector: 'app-file-upload',
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.scss'],
  standalone: false,
})
export class FileUploadComponent implements OnInit, OnDestroy, OnChanges {
  @ViewChild('fileInput') fileInput: ElementRef;
  @Input() parentFolderId: string = '';
  @Input() institution: boolean; //set folders/files created here under an institution or not
  @Input() disabled = false;
  @Input() forceUnique = false;
  @Input() disabledText: string = 'disabled';
  @Input() key: string = ''; //where this should be set to within the upload folder in s3
  @Input() acceptedFileTypes = 'csv';
  @Input() allowMultiple = false;
  @Input() centerField = false;
  @Input() content = '';
  @Input() canUpload = false; //manual upload button to be clicked to handle uploading from here to s3
  @Input() autoUpload = false; //attempt upload to s3 as soon as its dropped/selected
  @Input() uploadRoute = '';
  @Input() addMissingFolders: boolean; //attempt to find folders in s3 from the key and add them if missing in the data table as filefolders
  @Input() triggerUpload$: Subject<void>; // Subject to trigger uploads from parent component

  @Output() uploaded = new EventEmitter<UploadFile[]>();
  @Output() uploadProgress = new EventEmitter<UploadFile[]>();
  @Output() uploadComplete = new EventEmitter<UploadFile[]>();
  @Output() uploadStarted = new EventEmitter();

  @Input() files: UploadFile[] = [];
  public isUploading: boolean;
  private subscription = new Subscription();
  private hasAttemptedMissingFolders: boolean;

  // Check if all files have been fully uploaded and database entries created
  get allFilesUploaded(): boolean {
    return (
      this.files.length > 0 &&
      this.files.every(
        file =>
          (file.progress === 100 && file.databaseEntryAttempt) || file.error
      )
    );
  }

  constructor(
    private uploadService: UploadService,
    private notifications: NotificationsService,
    private http: HttpUtilService
  ) {}

  ngOnInit() {
    if (this.triggerUpload$) {
      this.subscription.add(
        this.triggerUpload$.subscribe(() => {
          this.uploadFiles();
        })
      );
    }
  }

  ngOnChanges(changes) {
    // avoid input not able to take previous file attempted after the files input may have been reset by parent being a two-way bound field
    if (!changes?.files?.length) {
      if (this.fileInput?.nativeElement) {
        this.fileInput.nativeElement.value = '';
      }
    }
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input?.files && input?.files?.length) {
      let newFiles = Array.from(input.files)
        .map(file => this.extendFile(file))
        .filter(file => !this?.files?.find(_file => file.name == _file.name));

      if (this.allowMultiple) {
        this.files = [...this.files, ...newFiles];
      } else {
        this.files = newFiles.length ? [newFiles[0]] : [];
      }
      this.uploaded.emit(this.files);

      if (this.autoUpload) {
        this.uploadFiles();
      }
    }
  }

  // convert a standard File to our extended UploadFile
  private extendFile(file: File): UploadFile {
    // clean the file name by trimming whitespace and replacing spaces with underscores
    const cleanFileName = file.name.trim().replace(/\s+/g, '_');

    const cleanedFile = new File([file], cleanFileName, {
      type: file.type,
      lastModified: file.lastModified,
    });

    return Object.assign(cleanedFile, {
      progress: 0,
      isUploading: false,
      error: null,
      key: null,
      databaseEntryAttempt: false,
    }) as UploadFile;
  }

  onFileDropped(event: DragEvent) {
    event.preventDefault();
    if (event.dataTransfer && event.dataTransfer.files.length) {
      const validFiles = this.filterValidFiles(
        Array.from(event.dataTransfer.files)
      )
        .map(file => this.extendFile(file))
        .filter(file => !this?.files?.find(_file => file.name == _file.name));

      if (validFiles.length) {
        let newFiles: UploadFile[] = validFiles;

        if (this.allowMultiple) {
          this.files = [...this.files, ...newFiles];
        } else {
          this.files = newFiles.length ? [newFiles[0]] : [];
        }

        this.uploaded.emit(this.files);

        if (this.autoUpload) {
          this.uploadFiles();
        }
      }
    }
  }

  private filterValidFiles(files: File[]): File[] {
    if (!this.acceptedFileTypes || this.acceptedFileTypes === '*') {
      return files;
    }

    const acceptedTypes = this.acceptedFileTypes.split(',').map(type => {
      return type.trim().startsWith('.')
        ? type.trim().substring(1)
        : type.trim();
    });
    let hasFiltered;

    const validFiles = files.filter(file => {
      const fileExt = file.name.split('.').pop()?.toLowerCase() || '';
      const result = acceptedTypes.some(type => {
        if (type.includes('/')) {
          const [category, subtype] = type.split('/');
          const [fileCategory] = (file.type || '').split('/');
          return subtype === '*'
            ? fileCategory === category
            : file.type === type;
        }
        return fileExt === type.toLowerCase();
      });
      if (!result) hasFiltered = true;

      return result;
    });

    if (hasFiltered)
      this.notifications.popNotification(
        '',
        'Files must be ' + JSON.stringify(this.acceptedFileTypes)
      );
    return validFiles;
  }

  onDragOver(event: Event) {
    event.preventDefault();
  }

  onDragLeave(event: Event) {
    event.preventDefault();
  }

  removeFile(event: Event, index: number) {
    event.preventDefault();
    event.stopPropagation();

    if (this.files[index]?.isUploading) return;

    if (index >= 0 && index < this.files.length) {
      this.files = this.files.filter((_, i) => i !== index);
      this.uploaded.emit(this.files);

      // reset the file input to allow re-selection of the same file
      if (this.fileInput?.nativeElement) {
        this.fileInput.nativeElement.value = '';
      }
    }
  }

  updateDataForUploadedFile(response): Observable<any> {
    let path = '';
    let body: any = {
      type: 'files',
      data: {
        title: response?.fileName,
        url: response?.eurl,
        size: response?.size,
        type: response?.fileref?.type,
      },
    };

    // if no parent folder was created and found here and intended for the institution, set it as such then
    if (this.institution && (!response.parentFolderId || this.parentFolderId)) {
      body.data.institution = true;
    }

    // set for newly created  folder in the upload request
    if (response.parentFolderId) {
      path += `/filefolders/${response.parentFolderId}`;
    } else if (this.parentFolderId) {
      // set for folder set in input
      path += `/filefolders/${this.parentFolderId}`;
    }
    path += '/files/' + response?.existing?.[0]?.id;
    const url = `${environment.apiRoot}${path}`;
    return this.http.request('PUT', url, body, null, true);
  }

  // insert file into data table
  createDataForUploadedFile(response): Observable<any> {
    let path = '';
    let body: any = {
      type: 'files',
      data: {
        title: response?.fileName,
        url: response?.eurl,
        size: response?.size,
        type: response?.fileref?.type,
      },
    };

    // set for newly created  folder in the upload request
    if (response.parentFolderId) {
      path += `/filefolders/${response.parentFolderId}`;
    } else if (this.parentFolderId) {
      // set for folder set in input
      path += `/filefolders/${this.parentFolderId}`;
    }
    // if no parent folder was created and found here and intended for the institution, set it as such then
    if (this.institution && (!response.parentFolderId || this.parentFolderId)) {
      body.data.institution = true;
    }
    path += '/files';
    const url = `${environment.apiRoot}${path}`;
    return this.http.request('POST', url, body, null, true);
  }

  triggerUpload($event: Event) {
    if ($event) {
      $event.preventDefault();
      $event.stopPropagation();
    }
    this.uploadFiles();
  }

  private createDatabaseEntryIfUploadComplete(
    response: any,
    fileName: string
  ): Observable<any> {
    if (response?.progress !== 100 || response?.error) {
      return of(response);
    }

    const fileIndex = this.files.findIndex(f => f.name === fileName);
    if (fileIndex === -1) {
      return of(response);
    }

    if (this.files[fileIndex].databaseEntryAttempt) {
      return of(response);
    }

    this.files[fileIndex].progress = 99;
    this.uploadProgress.emit(this.files);

    const dbRequest$ = response?.existing?.[0]?.id
      ? this.updateDataForUploadedFile(response)
      : this.createDataForUploadedFile(response);

    return dbRequest$.pipe(
      map(() => {
        this.files[fileIndex].databaseEntryAttempt = true;
        this.files[fileIndex].progress = 100;
        this.uploadProgress.emit(this.files);
        return response;
      }),
      catchError(error => {
        this.files[fileIndex].error = error;
        return of({ ...response, error });
      })
    );
  }

  // upload all files that have yet to be uploaded
  uploadFiles() {
    const uploadFiles = [...this.files].filter(
      file => !file.isUploading && file.progress === 0 && !file.error
    );

    if (!uploadFiles?.length || this.isUploading) return;
    this.uploadStarted.emit();
    this.isUploading = true;

    // If addMissingFolders is true, upload files sequentially initially to avoid missing parent folder name creation dupes
    if (this.addMissingFolders) {
      this.uploadFilesSequentially(uploadFiles);
    } else {
      this.uploadFilesInParallel(uploadFiles);
    }
  }

  private uploadFilesSequentially(uploadFiles: UploadFile[]) {
    if (uploadFiles.length === 0) {
      this.uploadComplete.emit(this.files);
      this.isUploading = false;
      return;
    }

    // start the first file upload immediately
    const firstFile = uploadFiles[0];
    const remainingFiles = uploadFiles.slice(1);

    firstFile.isUploading = true;
    remainingFiles.forEach(file => (file.isUploading = true));
    const firstFileIndex = this.files.findIndex(f => f.name === firstFile.name);
    let qp = [];
    if (this.addMissingFolders && !this.hasAttemptedMissingFolders) {
      this.hasAttemptedMissingFolders = true;
      qp.push('add_missing_folders=true');
    }
    if (this.institution) qp.push('institution=true');
    if (firstFileIndex > -1) {
      this.files[firstFileIndex].isUploading = true;
    }

    let remainingFilesStarted = false;

    // upload the first file
    this.uploadService
      .uploadSingle(firstFile, this.key, null, this.forceUnique, qp.join('&'))
      .pipe(
        catchError(error => of({ progress: 0, file: firstFile.name, error })),
        exhaustMap(response =>
          this.createDatabaseEntryIfUploadComplete(response, firstFile.name)
        )
      )
      .subscribe({
        next: progress => {
          const fileIndex = this.files.findIndex(
            f => f.name === firstFile.name
          );

          if (fileIndex > -1) {
            const file = this.files[fileIndex];
            const s3CompletePendingDbEntry =
              file.progress === 99 &&
              progress.progress === 100 &&
              !file.databaseEntryAttempt;

            if (!s3CompletePendingDbEntry) {
              file.progress = progress.progress;
            }

            if (progress.error) {
              file.error = progress.error;
            }
          }
          this.uploadProgress.emit(this.files);

          // Start remaining files after first progress emission (indicating upload has started and the parent folder for the remaining files has been created)
          if (
            remainingFiles.length > 0 &&
            !remainingFilesStarted &&
            progress.progress > 0
          ) {
            remainingFilesStarted = true;
            this.uploadFilesInParallel(remainingFiles, true);
          }
        },
        complete: () => {
          const fileIndex = this.files.findIndex(
            f => f.name === firstFile.name
          );
          if (fileIndex > -1) {
            this.files[fileIndex].isUploading = false;
          }
        },
        error: error => {
          const fileIndex = this.files.findIndex(
            f => f.name === firstFile.name
          );
          if (fileIndex > -1) {
            this.files[fileIndex].error = error;
            this.files[fileIndex].isUploading = false;
          }
        },
      });

    // If there's only one file, complete the upload when it's done
    if (remainingFiles.length === 0) {
      const checkCompletion = () => {
        const firstFileIndex = this.files.findIndex(
          f => f.name === firstFile.name
        );
        if (firstFileIndex > -1) {
          const file = this.files[firstFileIndex];
          if (
            (file.progress === 100 && file.databaseEntryAttempt) ||
            file.error
          ) {
            this.uploadComplete.emit(this.files);
            this.isUploading = false;
            return;
          }
        }
        setTimeout(checkCompletion, 100);
      };
      checkCompletion();
    }
  }

  private uploadFilesInParallel(
    uploadFiles: UploadFile[],
    isPartialUpload = false
  ) {
    // mark all files as uploading
    uploadFiles.forEach(file => {
      file.isUploading = true;
      const fileIndex = this.files.findIndex(f => f.name === file.name);
      if (fileIndex > -1) {
        this.files[fileIndex].isUploading = true;
      }
    });

    // observable per file upload that handles creating
    const uploads$ = uploadFiles.map(file => {
      let qp = [];
      if (this.addMissingFolders) {
        // avoid attempting creating multiple folders
        qp.push('add_missing_folders=true');
      }
      if (this.institution) qp.push('institution=true');
      return this.uploadService
        .uploadSingle(file, this.key, null, this.forceUnique, qp.join('&'))
        .pipe(
          catchError(error => of({ progress: 0, file: file.name, error })),
          exhaustMap(response =>
            this.createDatabaseEntryIfUploadComplete(response, file.name)
          )
        );
    });

    // process all uploads in parallel
    let completedUploads = 0;
    const totalUploads = uploads$.length;

    uploads$.forEach((uploadObservable, index) => {
      const file = uploadFiles[index];
      uploadObservable.subscribe({
        next: progress => {
          const fileIndex = this.files.findIndex(f => f.name === file.name);

          if (fileIndex > -1) {
            const trackedFile = this.files[fileIndex];
            const s3CompletePendingDbEntry =
              trackedFile.progress === 99 &&
              progress.progress === 100 &&
              !trackedFile.databaseEntryAttempt;

            if (!s3CompletePendingDbEntry) {
              trackedFile.progress = progress.progress;
            }

            if (progress.error) {
              trackedFile.error = progress.error;
            }
          }
          this.uploadProgress.emit(this.files);
        },
        complete: () => {
          // Only mark as completed when the entire observable chain is complete
          const fileIndex = this.files.findIndex(f => f.name === file.name);
          if (fileIndex > -1) {
            this.files[fileIndex].isUploading = false;
            completedUploads++;
          }

          // Check if all uploads are complete (including database entry creation)
          if (completedUploads === totalUploads) {
            // If this is a partial upload (called from sequential), check if all files are done
            if (isPartialUpload) {
              this.checkAllUploadsComplete();
            } else {
              this.uploadComplete.emit(this.files);
              this.isUploading = false;
            }
          }
        },
        error: error => {
          const fileIndex = this.files.findIndex(f => f.name === file.name);
          if (fileIndex > -1) {
            this.files[fileIndex].error = error;
            this.files[fileIndex].isUploading = false;
            completedUploads++;
          }

          // Check if all uploads are complete (including database entry creation)
          if (completedUploads === totalUploads) {
            // If this is a partial upload (called from sequential), check if all files are done
            if (isPartialUpload) {
              this.checkAllUploadsComplete();
            } else {
              this.uploadComplete.emit(this.files);
              this.isUploading = false;
            }
          }
        },
      });
    });
  }

  private checkAllUploadsComplete() {
    // Check if all files in the component are complete (both upload and database entry)
    const allFilesComplete = this.files.every(
      file => (file.progress === 100 && file.databaseEntryAttempt) || file.error
    );

    if (allFilesComplete) {
      console.log('All files completed, emitting uploadComplete');
      this.uploadComplete.emit(this.files);
      this.isUploading = false;
    } else {
      // If not all files are complete, check again in 100ms
      setTimeout(() => {
        this.checkAllUploadsComplete();
      }, 100);
    }
  }
}
